import DescriptionIcon from "@mui/icons-material/Description";
import PhotoCameraBackIcon from "@mui/icons-material/PhotoCameraBack";
import { Box, ListItemIcon } from "@mui/material";
import type { FieldPath, FieldValues, PathValue } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";
import { DocumentListItemText } from "./DocumentListItemText";
import type { Document, UploadItemProps } from "./types";
import { UploadActions } from "./UploadActions";
import { UploadListItem } from "./UploadContent";
import { UploadPreview } from "./UploadPreview";
import { UploadProgress } from "./UploadProgress";
import { useDocumentRow } from "./use-document-row";

const EMPTY_DOCUMENT: Document = { key: "", url: "", type: "" };

/** Marks system-generated documents so they are distinguishable from uploads. */
const GENERATED_SUFFIX = " - AG";

function toUploadDocument(value: unknown): Document {
  if (value === null || value === undefined || typeof value !== "object") {
    return EMPTY_DOCUMENT;
  }
  const v = value as Record<string, unknown>;
  return {
    label: typeof v.label === "string" ? v.label : undefined,
    name: typeof v.name === "string" ? v.name : undefined,
    key: typeof v.key === "string" ? v.key : "",
    url: typeof v.url === "string" ? v.url : "",
    type: typeof v.type === "string" ? v.type : undefined,
    docId: (v.docId as string | number | undefined) ?? (v.id as string | number | undefined),
    tempObjectUrl: typeof v.tempObjectUrl === "string" ? v.tempObjectUrl : undefined,
    version_status: typeof v.version_status === "string" ? v.version_status : undefined,
    document_state: typeof v.document_state === "string" ? v.document_state : undefined,
    rejectionReason: typeof v.rejectionReason === "string" ? v.rejectionReason : undefined,
    additionalInfo: typeof v.additionalInfo === "string" ? v.additionalInfo : undefined,
    updatedAt: typeof v.updatedAt === "string" ? v.updatedAt : undefined,
    metadata:
      v.metadata !== null && v.metadata !== undefined && typeof v.metadata === "object"
        ? (v.metadata as Record<string, unknown>)
        : undefined,
  };
}

export function UploadItem({
  value,
  meta,
  canEdit = true,
  error,
  handleDelete,
  handleFileUploaded,
  showStatus = false,
  showGeneratedSuffix = false,
}: UploadItemProps) {
  const {
    isMobile,
    openPreview,
    setOpenPreview,
    isImage,
    isPDF,
    loading,
    uploadProgress,
    documentTitle,
    hasDocument,
    updatedAt,
    isDragDropEnabled,
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    handleDownload,
    actions,
  } = useDocumentRow({ value, meta, mode: "upload", canEdit, onFileUploaded: handleFileUploaded });

  return (
    <>
      <UploadListItem
        {...getRootProps()}
        hasDocument={hasDocument}
        isDragActive={isDragActive}
        isDragDropEnabled={isDragDropEnabled}
        loading={loading}
      >
        <input {...getInputProps()} style={{ display: "none" }} />
        <ListItemIcon>
          {isImage ? <PhotoCameraBackIcon /> : isPDF ? <DescriptionIcon /> : null}
        </ListItemIcon>

        <DocumentListItemText
          documentTitle={documentTitle}
          titleSuffix={showGeneratedSuffix && value.origin === "SYSTEM" ? GENERATED_SUFFIX : ""}
          error={error}
          isImage={isImage}
          isPDF={isPDF}
          updatedAt={updatedAt}
          versionStatus={value?.version_status ?? undefined}
          documentState={value?.document_state}
          rejectionReason={value?.rejectionReason ?? undefined}
          additionalInfo={value?.additionalInfo ?? undefined}
          handledBy={value?.handledBy}
          expiresAt={value?.expiresAt}
          showStatus={showStatus}
        />

        {loading && (
          <Box sx={{ width: isMobile ? "100%" : 200 }}>
            <UploadProgress loading={loading} value={uploadProgress} />
          </Box>
        )}

        <UploadActions
          canChange={actions.canChange}
          canDelete={actions.canDelete}
          canOpen={actions.canOpen}
          canDownload={actions.canDownload}
          id={value.key}
          loading={loading}
          onChange={open}
          onDelete={() => handleDelete?.(value.key)}
          onDownload={handleDownload}
          onOpen={() => setOpenPreview(true)}
          readyOnly={!canEdit}
        />
      </UploadListItem>

      {hasDocument && !loading && (
        <UploadPreview
          document={{ ...value, url: actions.documentUrl }}
          isImage={isImage}
          onClose={() => setOpenPreview(false)}
          open={openPreview}
        />
      )}
    </>
  );
}

export type UploadFormItemProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = {
  name: TName;
  meta: import("./types").UploadMetadata;
  canEdit?: boolean;
  handleDelete: (key: string) => void;
  handleEdit?: (file: PathValue<TFieldValues, TName>) => void;
};

export function UploadFormItem<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  meta,
  canEdit = true,
  handleDelete,
  handleEdit,
}: UploadFormItemProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    control,
    name,
    rules: { required: "This field is required" },
  });

  const value = toUploadDocument(field.value);

  const handleFileUploaded = (file: Document) => {
    const next = file as PathValue<TFieldValues, TName>;
    field.onChange(next);
    handleEdit?.(next);
  };

  return (
    <UploadItem
      canEdit={canEdit}
      error={error}
      handleDelete={handleDelete}
      handleFileUploaded={handleFileUploaded}
      meta={meta}
      value={value}
    />
  );
}
